import { useEffect, useRef } from 'react';

const DRAW_MS = 1100;
// cubic-bezier(0.65, 0, 0.35, 1) evaluated manually per frame (see easeInOut below) --
// matches the same curve, just computed in JS since this is a rAF-driven draw,
// not a CSS animation (see the comment on the draw effect for why).
const easeInOut = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

// The Stocker wordmark's "o" is a stylized eye (see public/StockerLogo.png).
// This isolates that eye glyph as a standalone loading indicator: the lids
// trace themselves in once, then the pupil fades in and a thin accent arc
// spins continuously around it for as long as loading persists. Replaces
// the generic ring+dot spinner (animate-spin ring + a separate pulsing dot)
// used for the dashboard's and other components' loading states.
//
// The draw-in is driven by a direct rAF loop rather than a CSS
// transition/animation. Both were tried first: a transition needs a real
// painted frame between the "hidden" and "revealed" style writes to have
// anything to interpolate from, which a mount-time style flip doesn't
// reliably get; a CSS @keyframes animation whose from/to values are only
// known after measuring the path (so injected via a <style> tag after
// mount) was inconsistent about starting its timeline from that injection
// point. Computing stroke-dashoffset directly from elapsed time removes
// both failure modes -- there's no ambiguity about when "frame zero" is.
const StockerEyeLoader = ({ size = 48, className = '' }) => {
  const lidPathRef = useRef(null);

  useEffect(() => {
    const path = lidPathRef.current;
    if (!path) return;

    const length = path.getTotalLength();
    path.style.strokeDasharray = String(length);
    path.style.strokeDashoffset = String(length);

    let raf;
    const start = performance.now();
    const tick = (now) => {
      const t = Math.min(1, (now - start) / DRAW_MS);
      path.style.strokeDashoffset = String(length * (1 - easeInOut(t)));
      if (t < 1) {
        raf = requestAnimationFrame(tick);
      }
    };
    raf = requestAnimationFrame(tick);

    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <div
      className={`stocker-eye-loader ${className}`}
      style={{ width: size, height: size }}
      role="status"
      aria-label="Loading"
    >
      <style>{`
        .stocker-eye-loader svg { display: block; width: 100%; height: 100%; }
        .stocker-eye-pupil {
          transform-origin: 12px 12px;
          animation: stocker-eye-pupil-in 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) 1.0s both;
        }
        .stocker-eye-spin {
          transform-origin: 12px 12px;
          opacity: 0;
          animation: stocker-eye-spin-rotate 1.3s linear infinite,
                     stocker-eye-spin-in 0.3s ease-out 1.1s forwards;
        }
        @keyframes stocker-eye-pupil-in {
          from { transform: scale(0); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
        @keyframes stocker-eye-spin-in {
          to { opacity: 1; }
        }
        @keyframes stocker-eye-spin-rotate {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @media (prefers-reduced-motion: reduce) {
          .stocker-eye-pupil, .stocker-eye-spin { animation-duration: 0.01ms !important; animation-iteration-count: 1 !important; }
        }
      `}</style>
      <svg viewBox="0 0 24 24" fill="none">
        <defs>
          <linearGradient id="stocker-eye-gradient" x1="2" y1="5" x2="22" y2="19" gradientUnits="userSpaceOnUse">
            <stop offset="0%" stopColor="#a78bfa" />
            <stop offset="50%" stopColor="#818cf8" />
            <stop offset="100%" stopColor="#60a5fa" />
          </linearGradient>
        </defs>

        {/* Lids -- draw themselves in once on mount, see the rAF loop above */}
        <path
          ref={lidPathRef}
          d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"
          stroke="url(#stocker-eye-gradient)"
          strokeWidth={1.6}
          strokeLinecap="round"
          strokeLinejoin="round"
        />

        {/* Pupil -- pops in once the lids finish drawing */}
        <circle className="stocker-eye-pupil" cx="12" cy="12" r="2.6" fill="url(#stocker-eye-gradient)" />

        {/* Accent arc -- spins continuously while loading persists */}
        <circle
          className="stocker-eye-spin"
          cx="12"
          cy="12"
          r="4.6"
          stroke="url(#stocker-eye-gradient)"
          strokeWidth="1"
          strokeLinecap="round"
          strokeDasharray="5 24.9"
          fill="none"
        />
      </svg>
    </div>
  );
};

export default StockerEyeLoader;
